import type {
  UserVoucherItem,
  UserVoucherSummary,
  UserVouchersResponse,
} from './types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function getDefaultSummary(): UserVoucherSummary {
  return {
    holderCount: 0,
    circulatingVouchers: 0,
    bannedCount: 0,
  };
}

function normalizeSummary(raw: unknown): UserVoucherSummary {
  const fallback = getDefaultSummary();
  if (!isRecord(raw)) return fallback;

  return {
    holderCount: Number(raw.holderCount ?? raw.holder_count ?? 0) || 0,
    circulatingVouchers:
      Number(raw.circulatingVouchers ?? raw.circulating_vouchers ?? 0) || 0,
    bannedCount: Number(raw.bannedCount ?? raw.banned_count ?? 0) || 0,
  };
}

function normalizeItem(raw: unknown): UserVoucherItem | null {
  if (!isRecord(raw)) return null;

  return {
    rank: Number(raw.rank ?? 0) || 0,
    userId: String(raw.userId ?? raw.user_id ?? ''),
    address: String(raw.address ?? ''),
    currentVouchers:
      Number(raw.currentVouchers ?? raw.current_vouchers ?? 0) || 0,
    banned: Boolean(raw.banned ?? false),
  };
}

export function normalizeUserVouchers(raw: unknown): UserVouchersResponse {
  const source = isRecord(raw) && isRecord(raw.data) ? raw.data : raw;

  if (!isRecord(source)) {
    return {
      summary: getDefaultSummary(),
      items: [],
      totalCount: 0,
    };
  }

  const itemsRaw = Array.isArray(source.items)
    ? source.items
    : Array.isArray(source.list)
      ? source.list
      : [];

  return {
    summary: normalizeSummary(source.summary),
    items: itemsRaw
      .map(normalizeItem)
      .filter((item): item is UserVoucherItem => Boolean(item)),
    totalCount: Number(source.totalCount ?? source.total_count ?? itemsRaw.length) || 0,
  };
}

export function formatInt(value?: number | string | null) {
  const num = Number(value ?? 0);
  return new Intl.NumberFormat('en-US').format(Number.isFinite(num) ? num : 0);
}

export function shortenWallet(address?: string | null) {
  if (!address) return '--';
  if (address.length <= 10) return address;
  return `${address.slice(0, 5)}...${address.slice(-4)}`;
}

export function getErrorMessage(error: unknown) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;

  if (isRecord(error)) {
    if (typeof error.message === 'string') return error.message;
    if (typeof error.error === 'string') return error.error;
    if (isRecord(error.response) && isRecord(error.response.data)) {
      const data = error.response.data;
      if (typeof data.message === 'string') return data.message;
      if (typeof data.error === 'string') return data.error;
    }
  }

  return String(error);
}
